import { authHeaders } from '../../../core/api/authHeaders';

/**
 * Tell the customer their order is confirmed, once, when it is added to dispatch.
 *
 * The DECISION is the server's, not ours: it re-reads the order, re-checks that it
 * really is in dispatch, and claims the send in a transaction on the order itself. The
 * desk may click Assign twice, two staff may assign the same order, a page may remount
 * — none of that can produce a second message, because this call is only a nudge.
 *
 * It is therefore safe to fire and forget, and a failure here must NEVER block or undo
 * a courier assignment: the parcel still goes out, the customer just was not texted.
 */

const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

/**
 * @param {string} orderKey the Firebase key of the order
 * @returns {Promise<{status: string, reason?: string}>} never throws
 */
export async function sendOrderConfirmationSms(orderKey) {
    const key = String(orderKey || '').trim();
    if (!key) return { status: 'skipped', reason: 'no_key' };
    try {
        const r = await fetch(`${PROXY}/api/orders/${encodeURIComponent(key)}/confirmation-sms`, {
            method: 'POST',
            headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
        });
        const data = await r.json().catch(() => null);
        return data || { status: 'failed', reason: `HTTP ${r.status}` };
    } catch (e) {
        // Offline, blocked, proxy down — the assignment itself already succeeded.
        console.warn('[orderConfirmSms] could not request the confirmation SMS:', e.message);
        return { status: 'failed', reason: e.message };
    }
}

/**
 * The same thing for a bulk assign, a few at a time.
 *
 * A bulk assign can be hundreds of orders; firing hundreds of requests at once would
 * hit the proxy's rate limit and get some of them refused — which, since the server
 * only claims a send once it is actually sending, would simply leave those customers
 * untexted. Four at a time is polite and still finishes a large batch quickly.
 *
 * @returns {Promise<{sent: number, skipped: number, failed: number}>}
 */
export async function sendOrderConfirmationSmsBatch(orderKeys = [], { concurrency = 4 } = {}) {
    const keys = orderKeys.filter(Boolean);
    const totals = { sent: 0, skipped: 0, failed: 0 };
    let next = 0;
    const worker = async () => {
        while (next < keys.length) {
            const mine = keys[next++];
            const { status } = await sendOrderConfirmationSms(mine);
            if (status === 'sent') totals.sent++;
            else if (status === 'failed') totals.failed++;
            else totals.skipped++;          // skipped, not_configured
        }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, keys.length) }, worker));
    return totals;
}

export default { sendOrderConfirmationSms, sendOrderConfirmationSmsBatch };
